import * as React from "react";
import { useApiService, useTl } from "../../../../hooks";
import { TempRSHRequestApi, ERequestState, TempRSHRequestGridDto, TempRSHRequestSearch } from "../../../../api";
import { useHistory } from "react-router";
import { useGlobalData, useEventsContext, useDialog } from "../../../../contexts";
import { Checkbox, Intent } from "@blueprintjs/core";
import { IDataTableColumn, ButtonContainer, showSuccess, showError } from "nsitools-react";
import { ETLCodes } from "../../../../locales";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../../ERoutes";
import { ViewButton, EditButton, SearchTablePage, AddButton, CloseButton } from "../../../../components";

export interface ITempRSHRequestListProps {}

export const TempRSHRequestList: React.FunctionComponent<ITempRSHRequestListProps> = (props) => {
  const api = useApiService(TempRSHRequestApi);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const { dispatchEvent } = useEventsContext();
  const { showDialog } = useDialog();

  const [selectAll, setselectAll] = React.useState(false);
  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  // TODO CRR : this columnCheckBox component is redeclared thousand times accross the code base => Extract a component please !!!
  const columnCheckBox = React.useCallback(
    (row: TempRSHRequestGridDto) => {
      const disabled = row.requestStateCode !== ERequestState.PAID;
      if (!disabled) {
        closableIdsList.push(row.requestId);
      }
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
          disabled={disabled}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: TempRSHRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.tempRsh}/${row.dogTempOwnershipChangeRequestId}/detail/view`)}
            />

            <EditButton
              minimal={true}
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              onClick={() => history.push(`${ERoutes.tempRsh}/${row.dogTempOwnershipChangeRequestId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: TempRSHRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: TempRSHRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
            {row.dog}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: TempRSHRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.contactId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.TemporaryOwner),
        fieldName: "temporaryOwner",
        render: (row: TempRSHRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.temporaryOwnerId}/detail/view`}>
            {row.temporaryOwner}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [columnCheckBox, history, t, toggleSelectAll]
  );

  const closeRequests = React.useCallback(
    (requestList) => {
      if (selectionList.length === 0) {
        showDialog({
          message: t(ETLCodes.CloseRequestSelectionEmpty),
          title: t(ETLCodes.CloseRequests),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseRequestConfirmationMessage),
          title: t(ETLCodes.CloseRequests),
          onConfirmed: async () => {
            try {
              await requestList.forEach((element) => {
                api.close(element);
              });
              selectionList.splice(0, selectionList.length);
              dispatchEvent("RefreshSearchTables", 0);
              showSuccess(t(ETLCodes.RequestsClosedSuccess));
            } catch (error) {
              showError(t(ETLCodes.Error));
            }
          },
        });
      }
    },
    [api, dispatchEvent, selectionList, showDialog, t]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.tempRsh}/0/detail/edit`);
  }, [history]);
  const search = React.useCallback(
    (sObj) =>
      api.baseSearch(
        TempRSHRequestSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(
          TempRSHRequestSearch.fromJS({
            ...sObj,
            languageId: currentLanguage.languageId,
          })
        )
      }
      sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.TempRSHRequests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => addItemFunction()} />
          <CloseButton text={t(ETLCodes.Close)} onClick={() => closeRequests(selectionList)} />
        </div>
      }
    />
  );
};
